import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  Boxes,
  Building2,
  CalendarDays,
  FolderKanban,
  KeyRound,
  Loader2,
  Package,
  Receipt,
  UserSquare2,
  Wallet,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { useCompanyList } from "@/modules/companies/hooks/useCompanies";
import { useProductList } from "@/modules/products/hooks/useProducts";
import { useProjectList } from "@/modules/projects/hooks/useProjects";
import { useMeetingList } from "@/modules/meetings/hooks/useMeetings";
import { useBudgetList } from "@/modules/budgets/hooks/useBudgets";
import { useUserList } from "@/modules/users/hooks/useUsers";
import { useVaultList } from "@/modules/vault/hooks/useVault";
import { useAssetList } from "@/modules/assets/hooks/useAssets";
import { useContactList } from "@/modules/contacts/hooks/useContacts";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useCategoryItems } from "@/modules/dropdowns/hooks/useDropdowns";
import { PhaseStagePicker } from "@/components/shared/PhaseStagePicker";
import { ExternalCompanyPicker } from "@/components/shared/ExternalCompanyPicker";
import { useCreateExpense } from "../hooks/useExpenses";
import type { ExpenseListItem } from "../types";
import { formatCurrency } from "./CurrencyAmount";

type LockedFields = {
  companyId?: boolean;
  productId?: boolean;
  projectId?: boolean;
  budgetId?: boolean;
  meetingId?: boolean;
  accessVaultId?: boolean;
  assetId?: boolean;
  vendorContactId?: boolean;
};

type PrefillContext = {
  companyId?: string | null;
  productId?: string | null;
  projectId?: string | null;
  budgetId?: string | null;
  meetingId?: string | null;
  meetingTitle?: string | null;
  accessVaultId?: string | null;
  assetId?: string | null;
  vendorContactId?: string | null;
  locked?: LockedFields;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (code: string) => void;
  prefill?: PrefillContext;
  /** Pre-fill the vendor (External, systemCode=vendor) picker — opened from
   *  an External vendor company's Expenses tab. */
  defaultVendorCompanyId?: string;
  /** When set, the drawer opens in "Duplicate" mode: sticky context fields are
   *  copied from this record while transaction-specific fields are cleared.
   *  Submitting still creates a brand-new expense (new id/code); the source is
   *  untouched. */
  duplicateFrom?: ExpenseListItem;
};

export function NewExpenseDrawer({
  isOpen,
  onClose,
  onCreated,
  prefill,
  defaultVendorCompanyId,
  duplicateFrom,
}: Props) {
  const { user } = useAuth();
  const createExpense = useCreateExpense();
  const { data: categoryItems = [] } = useCategoryItems("expense_category");
  const { data: subcategoryItems = [] } = useCategoryItems(
    "expense_subcategory",
  );
  const { data: paymentItems = [] } = useCategoryItems(
    "expense_payment_method",
  );
  const { data: currencyItems = [] } = useCategoryItems("currency");
  const { data: usersResult } = useUserList({
    page: 1,
    limit: 100,
    status: "ACTIVE",
  });
  const allUsers = usersResult?.data ?? [];

  const lockCompany = !!prefill?.locked?.companyId && !!prefill?.companyId;
  const lockProduct = !!prefill?.locked?.productId && !!prefill?.productId;
  const lockProject = !!prefill?.locked?.projectId && !!prefill?.projectId;
  const lockBudget = !!prefill?.locked?.budgetId && !!prefill?.budgetId;
  const lockMeeting = !!prefill?.locked?.meetingId && !!prefill?.meetingId;
  const lockVault =
    !!prefill?.locked?.accessVaultId && !!prefill?.accessVaultId;
  const lockAsset = !!prefill?.locked?.assetId && !!prefill?.assetId;
  const lockVendorContact =
    !!prefill?.locked?.vendorContactId && !!prefill?.vendorContactId;

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [notes, setNotes] = useState("");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("AED");
  const [exchangeRate, setExchangeRate] = useState("1");
  const [expenseDate, setExpenseDate] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [paymentMethod, setPaymentMethod] = useState("");
  const [receiptUrl, setReceiptUrl] = useState("");
  const [selectedBudgetId, setSelectedBudgetId] = useState(
    prefill?.budgetId ?? "",
  );
  const [selectedCompanyId, setSelectedCompanyId] = useState(
    prefill?.companyId ?? "",
  );
  const [selectedProductId, setSelectedProductId] = useState(
    prefill?.productId ?? "",
  );
  const [selectedProjectId, setSelectedProjectId] = useState(
    prefill?.projectId ?? "",
  );
  const [selectedMeetingId, setSelectedMeetingId] = useState(
    prefill?.meetingId ?? "",
  );
  const [selectedVendorCompanyId, setSelectedVendorCompanyId] = useState(
    defaultVendorCompanyId ?? "",
  );
  const [selectedAccessVaultId, setSelectedAccessVaultId] = useState(
    prefill?.accessVaultId ?? "",
  );
  const [selectedAssetId, setSelectedAssetId] = useState(prefill?.assetId ?? "");
  const [selectedVendorContactId, setSelectedVendorContactId] = useState(
    prefill?.vendorContactId ?? "",
  );
  const [selectedPhaseId, setSelectedPhaseId] = useState<string | null>(null);
  const [selectedStageId, setSelectedStageId] = useState<string | null>(null);
  const [paidById, setPaidById] = useState<string>(user?.id ?? "");
  const [isReimbursable, setIsReimbursable] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.id && !paidById) setPaidById(user.id);
  }, [user?.id, paidById]);

  useEffect(() => {
    if (isOpen) {
      setSelectedCompanyId(prefill?.companyId ?? "");
      setSelectedProductId(prefill?.productId ?? "");
      setSelectedProjectId(prefill?.projectId ?? "");
      setSelectedBudgetId(prefill?.budgetId ?? "");
      setSelectedMeetingId(prefill?.meetingId ?? "");
      setSelectedVendorCompanyId(defaultVendorCompanyId ?? "");
      setSelectedAccessVaultId(prefill?.accessVaultId ?? "");
      setSelectedAssetId(prefill?.assetId ?? "");
      setSelectedVendorContactId(prefill?.vendorContactId ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    prefill?.companyId,
    prefill?.productId,
    prefill?.projectId,
    prefill?.budgetId,
    prefill?.meetingId,
    prefill?.accessVaultId,
    prefill?.assetId,
    prefill?.vendorContactId,
    defaultVendorCompanyId,
  ]);

  // Duplicate mode: copy sticky context from the source record, clear the
  // transaction-specific fields. Mutually exclusive with `prefill` in practice;
  // this effect runs after the prefill effect so its values win.
  useEffect(() => {
    if (!isOpen || !duplicateFrom) return;
    const d = duplicateFrom;
    setTitle(d.title ?? "");
    setDescription(d.description ?? "");
    setCategory(d.category ?? "");
    setSubcategory(d.subcategory ?? "");
    setNotes(d.notes ?? "");
    setCurrency(d.currency ?? "AED");
    setExchangeRate(String(d.exchangeRate ?? 1));
    setSelectedCompanyId(d.companyId ?? "");
    setSelectedVendorCompanyId(d.vendorCompanyId ?? "");
    setSelectedVendorContactId(d.vendorContactId ?? "");
    setSelectedProductId(d.productId ?? "");
    setSelectedProjectId(d.projectId ?? "");
    setSelectedPhaseId(d.phaseId ?? null);
    setSelectedStageId(d.stageId ?? null);
    setSelectedBudgetId(d.budgetId ?? "");
    setSelectedAccessVaultId(d.accessVaultId ?? "");
    setSelectedAssetId(d.assetId ?? "");
    setSelectedMeetingId(d.meetingId ?? "");
    setIsReimbursable(d.isReimbursable ?? false);
    // Cleared transaction details — user enters these fresh:
    setAmount("");
    setExpenseDate(new Date().toISOString().slice(0, 10));
    setPaidById("");
    setPaymentMethod("");
    setReceiptUrl("");
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, duplicateFrom]);

  const { data: companiesResult } = useCompanyList({
    page: 1,
    limit: 100,
    operationalOnly: true,
  });
  const companies = companiesResult?.data ?? [];

  const { data: productsResult } = useProductList({
    page: 1,
    limit: 100,
    companyId: selectedCompanyId || undefined,
  });
  const products = productsResult?.data ?? [];

  const { data: projectsResult } = useProjectList({
    page: 1,
    limit: 100,
    companyId: selectedCompanyId || undefined,
    productId: selectedProductId || undefined,
  });
  const projects = projectsResult?.data ?? [];

  const { data: meetingsResult } = useMeetingList({
    page: 1,
    limit: 100,
    projectId: selectedProjectId || undefined,
  });
  const meetings = meetingsResult?.data ?? [];

  const { data: budgetsResult } = useBudgetList({
    page: 1,
    limit: 100,
    status: "active",
    companyId: selectedCompanyId || undefined,
    productId: selectedProductId || undefined,
    projectId: selectedProjectId || undefined,
  });
  const budgets = budgetsResult?.data ?? [];

  const { data: vaultResult } = useVaultList({ page: 1, limit: 100 });
  const vaultEntries = vaultResult?.data ?? [];

  const { data: assetsResult } = useAssetList({ page: 1, limit: 100 });
  const assets = assetsResult?.data ?? [];

  // Vendor-contact picker: surface Contacts of type Vendor — the most useful
  // signal we have at the Contact level. The API additionally validates that
  // the contact's company is External when one is set.
  const { data: contactsResult } = useContactList({ page: 1, limit: 100 });
  const vendorContacts = useMemo(
    () =>
      (contactsResult?.data ?? []).filter(
        (c) =>
          c.id === selectedVendorContactId ||
          (c.status === "Active" && c.type === "Vendor"),
      ),
    [contactsResult?.data, selectedVendorContactId],
  );

  const amountNum = Number(amount) || 0;
  const rateNum = Number(exchangeRate) || 1;
  const baseAmount = useMemo(() => {
    if (currency === "AED") return amountNum;
    return amountNum * rateNum;
  }, [amountNum, currency, rateNum]);

  const filteredSubcategories = useMemo(
    () =>
      category
        ? subcategoryItems.filter((s) => s.parentValue === category)
        : [],
    [subcategoryItems, category],
  );

  function reset() {
    setTitle("");
    setDescription("");
    setCategory("");
    setSubcategory("");
    setNotes("");
    setAmount("");
    setCurrency("AED");
    setExchangeRate("1");
    setExpenseDate(new Date().toISOString().slice(0, 10));
    setPaymentMethod("");
    setReceiptUrl("");
    setSelectedBudgetId(prefill?.budgetId ?? "");
    setSelectedCompanyId(prefill?.companyId ?? "");
    setSelectedProductId(prefill?.productId ?? "");
    setSelectedProjectId(prefill?.projectId ?? "");
    setSelectedMeetingId(prefill?.meetingId ?? "");
    setSelectedVendorCompanyId(defaultVendorCompanyId ?? "");
    setSelectedAccessVaultId(prefill?.accessVaultId ?? "");
    setSelectedAssetId(prefill?.assetId ?? "");
    setSelectedVendorContactId(prefill?.vendorContactId ?? "");
    setSelectedPhaseId(null);
    setSelectedStageId(null);
    setPaidById(user?.id ?? "");
    setIsReimbursable(false);
    setError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !amount) return;
    if (amountNum <= 0) {
      setError("Amount must be positive");
      return;
    }
    setError(null);
    try {
      const res = await createExpense.mutateAsync({
        title: title.trim(),
        description: description.trim() || null,
        amount: amountNum,
        currency,
        exchangeRate: currency === "AED" ? 1 : rateNum,
        category: category || null,
        subcategory: subcategory || null,
        paymentMethod: paymentMethod || null,
        expenseDate: expenseDate
          ? new Date(expenseDate).toISOString()
          : new Date().toISOString(),
        receiptUrl: receiptUrl.trim() || null,
        notes: notes.trim() || null,
        budgetId: selectedBudgetId || null,
        companyId: selectedCompanyId || null,
        vendorCompanyId: selectedVendorCompanyId || null,
        vendorContactId: selectedVendorContactId || null,
        productId: selectedProductId || null,
        projectId: selectedProjectId || null,
        phaseId: selectedPhaseId,
        stageId: selectedStageId,
        meetingId: selectedMeetingId || null,
        accessVaultId: selectedAccessVaultId || null,
        assetId: selectedAssetId || null,
        paidById: paidById || null,
        isReimbursable,
      });
      onCreated?.(res.expense.code);
      reset();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create expense");
    }
  }

  if (!isOpen) return null;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[9998] bg-black/30"
        onClick={onClose}
      />
      <div
        className="fixed right-0 top-0 z-[9999] flex h-full w-[500px] flex-col border-l bg-white shadow-xl"
        style={{ borderColor: "hsl(210,30%,85%)" }}
      >
        <div
          className="flex items-center justify-between border-b px-6 py-4"
          style={{ borderColor: "hsl(210,30%,85%)" }}
        >
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              {duplicateFrom ? "Duplicate Expense" : "New Expense"}
            </h2>
            {duplicateFrom && (
              <p className="mt-0.5 text-xs text-muted-foreground">
                Duplicating from {duplicateFrom.code} — {duplicateFrom.title}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex flex-1 flex-col overflow-y-auto"
        >
          <div className="flex-1 space-y-6 p-6">
            <h3 className="text-sm font-semibold text-foreground">
              Expense Details
            </h3>

            <div className="space-y-2">
              <Label htmlFor="exp-title">
                Title <span className="text-destructive">*</span>
              </Label>
              <Input
                id="exp-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Flight to Dubai"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="exp-category">Category</Label>
              <Select
                id="exp-category"
                value={category}
                onChange={(e) => {
                  setCategory(e.target.value);
                  setSubcategory("");
                }}
              >
                <option value="">Uncategorized</option>
                {categoryItems.map((c) => (
                  <option key={c.id} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </Select>
            </div>

            {category && (
              <div className="space-y-2">
                <Label htmlFor="exp-subcategory">Subcategory</Label>
                <Select
                  id="exp-subcategory"
                  value={subcategory}
                  onChange={(e) => setSubcategory(e.target.value)}
                >
                  <option value="">Select subcategory...</option>
                  {filteredSubcategories.map((s) => (
                    <option key={s.id} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="exp-desc">Description</Label>
              <textarea
                id="exp-desc"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="exp-notes">Notes</Label>
              <textarea
                id="exp-notes"
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </div>

            <h3 className="text-sm font-semibold text-foreground">Amount</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="exp-amount">
                  Amount <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="exp-amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="exp-currency">Currency</Label>
                <Select
                  id="exp-currency"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                >
                  {currencyItems.length === 0 ? (
                    <option value="AED">AED</option>
                  ) : (
                    currencyItems.map((c) => (
                      <option key={c.id} value={c.value.toUpperCase()}>
                        {c.label}
                      </option>
                    ))
                  )}
                </Select>
              </div>
            </div>

            {currency !== "AED" && (
              <div className="space-y-2">
                <Label htmlFor="exp-rate">
                  Exchange Rate to AED
                </Label>
                <Input
                  id="exp-rate"
                  type="number"
                  step="0.000001"
                  min="0"
                  value={exchangeRate}
                  onChange={(e) => setExchangeRate(e.target.value)}
                />
                <p className="text-[11px] text-muted-foreground">
                  Enter how many AED = 1 {currency}. Example: 1 USD = 3.67 AED →
                  enter 3.67
                </p>
              </div>
            )}

            <div
              className="rounded-md border px-3 py-2 text-sm"
              style={{
                borderColor: "hsl(210,30%,85%)",
                background: "hsl(210,16%,97%)",
              }}
            >
              <span className="text-muted-foreground">Base Amount: </span>
              <span className="font-semibold text-foreground">
                {formatCurrency(baseAmount, "AED")}
              </span>
            </div>

            <h3 className="text-sm font-semibold text-foreground">Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="exp-date">
                  Expense Date <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="exp-date"
                  type="date"
                  value={expenseDate}
                  onChange={(e) => setExpenseDate(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="exp-receipt">Receipt URL</Label>
                <Input
                  id="exp-receipt"
                  value={receiptUrl}
                  onChange={(e) => setReceiptUrl(e.target.value)}
                  placeholder="Link to receipt (upload coming soon)"
                />
              </div>
            </div>

            <h3 className="text-sm font-semibold text-foreground">
              Payment &amp; Reimbursement
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="exp-paid-by">Paid By</Label>
                <select
                  id="exp-paid-by"
                  value={paidById}
                  onChange={(e) => setPaidById(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">Company (no individual payer)</option>
                  {allUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="exp-pay">Payment Method</Label>
                <Select
                  id="exp-pay"
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                >
                  <option value="">Not specified</option>
                  {paymentItems.map((p) => (
                    <option key={p.id} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={isReimbursable}
                onChange={(e) => setIsReimbursable(e.target.checked)}
                className="h-4 w-4"
              />
              <span className="text-foreground">
                This expense is reimbursable
              </span>
            </label>
            {isReimbursable && (
              <p className="text-[11px] text-muted-foreground">
                Record reimbursement payments individually from the expense's
                detail page after it's created.
              </p>
            )}

            <h3 className="text-sm font-semibold text-foreground">Association</h3>

            <div className="space-y-2">
              <Label htmlFor="exp-budget">Budget</Label>
              <div className="relative">
                <Wallet
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <select
                  id="exp-budget"
                  value={selectedBudgetId}
                  disabled={lockBudget}
                  onChange={(e) => setSelectedBudgetId(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm disabled:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">No budget</option>
                  {budgets.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.code} — {b.name}
                      {" "}({formatCurrency(b.remainingAmount, b.currency)} left)
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="exp-company">Company</Label>
              <div className="relative">
                <Building2
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <select
                  id="exp-company"
                  value={selectedCompanyId}
                  disabled={lockCompany}
                  onChange={(e) => {
                    setSelectedCompanyId(e.target.value);
                    setSelectedProductId("");
                    setSelectedProjectId("");
                  }}
                  className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm disabled:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">No company</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="exp-vendor">Vendor (External Company)</Label>
              <ExternalCompanyPicker
                id="exp-vendor"
                value={selectedVendorCompanyId}
                onChange={setSelectedVendorCompanyId}
                systemCodes={["vendor"]}
                placeholder="No vendor"
              />
              <p className="text-[11px] text-muted-foreground">
                Filtered to External companies with type=Vendor.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="exp-product">Product</Label>
              <div className="relative">
                <Package
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <select
                  id="exp-product"
                  value={selectedProductId}
                  disabled={lockProduct}
                  onChange={(e) => {
                    setSelectedProductId(e.target.value);
                    setSelectedProjectId("");
                  }}
                  className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm disabled:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">No product</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="exp-project">Project</Label>
              <div className="relative">
                <FolderKanban
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <select
                  id="exp-project"
                  value={selectedProjectId}
                  disabled={lockProject}
                  onChange={(e) => {
                    setSelectedProjectId(e.target.value);
                    setSelectedPhaseId(null);
                    setSelectedStageId(null);
                  }}
                  className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm disabled:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">No project</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {selectedProjectId && (
              <PhaseStagePicker
                projectId={selectedProjectId || null}
                phaseId={selectedPhaseId}
                stageId={selectedStageId}
                onPhaseChange={setSelectedPhaseId}
                onStageChange={setSelectedStageId}
              />
            )}

            <h3 className="text-sm font-semibold text-foreground">
              Related entities
              <span className="ml-2 text-[11px] font-normal text-muted-foreground">
                (all optional)
              </span>
            </h3>

            <div className="space-y-2">
              <Label htmlFor="exp-vault">Vault Entry</Label>
              <div className="relative">
                <KeyRound
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <select
                  id="exp-vault"
                  value={selectedAccessVaultId}
                  disabled={lockVault}
                  onChange={(e) => setSelectedAccessVaultId(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm disabled:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">No vault entry</option>
                  {vaultEntries.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.code} — {v.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="exp-asset">Asset</Label>
              <div className="relative">
                <Boxes
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <select
                  id="exp-asset"
                  value={selectedAssetId}
                  disabled={lockAsset}
                  onChange={(e) => setSelectedAssetId(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm disabled:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">No asset</option>
                  {assets.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.code} — {a.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="exp-vendor-contact">Vendor Contact</Label>
              <div className="relative">
                <UserSquare2
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <select
                  id="exp-vendor-contact"
                  value={selectedVendorContactId}
                  disabled={lockVendorContact}
                  onChange={(e) => setSelectedVendorContactId(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm disabled:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">No vendor contact</option>
                  {vendorContacts.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.fullName}
                      {c.companyName ? ` — ${c.companyName}` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Only vendor-type external contacts are shown. For a company-level
                vendor link, use the Vendor (External Company) picker above.
              </p>
            </div>

            {lockMeeting && prefill?.meetingTitle ? (
              <div className="space-y-2">
                <Label>Meeting</Label>
                <div
                  className="rounded-md border px-3 py-2 text-sm text-muted-foreground"
                  style={{
                    borderColor: "hsl(210,30%,85%)",
                    background: "hsl(210,16%,97%)",
                  }}
                >
                  {prefill.meetingTitle}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="exp-meeting">Meeting</Label>
                <div className="relative">
                  <CalendarDays
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                    strokeWidth={1.5}
                  />
                  <select
                    id="exp-meeting"
                    value={selectedMeetingId}
                    onChange={(e) => setSelectedMeetingId(e.target.value)}
                    className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                  >
                    <option value="">No meeting</option>
                    {meetings.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.code ?? ""} — {m.title}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {error && (
              <div
                className="rounded-md border px-3 py-2 text-sm"
                style={{
                  borderColor: "hsl(0,70%,80%)",
                  background: "hsl(0,70%,96%)",
                  color: "hsl(0,70%,35%)",
                }}
              >
                {error}
              </div>
            )}
          </div>

          <div
            className="flex items-center justify-end gap-3 border-t px-6 py-4"
            style={{ borderColor: "hsl(210,30%,85%)" }}
          >
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!title.trim() || !amount || createExpense.isPending}
            >
              {createExpense.isPending && (
                <Loader2
                  className="mr-1 h-4 w-4 animate-spin"
                  strokeWidth={1.5}
                />
              )}
              <Receipt className="mr-1 h-4 w-4" strokeWidth={1.5} />
              Submit Expense
            </Button>
          </div>
        </form>
      </div>
    </>,
    document.body,
  );
}
